import React from 'react';
import {
  Shield, Activity, TrendingUp, BarChart3,
  Plug, Lock, Layers,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const DataIngestionPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Data Ingestion',
      title: (
        <>
          Seamless data acquisition <em>for modern energy operations</em>
        </>
      ),
      lede: 'Leveraging OPC-DA/UA, MODBUS-TCIP/IP, HTTP/S, MQTT, Kafka, and REST APIs to collect, normalize, and integrate real-time data from diverse sensors and IoT assets.',
      image: '/assets/img/general/steptodown.com151755.jpg',
      imageAlt: 'Energy infrastructure generating operational data',
      actions: [
        { label: 'Get a demo', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '99.9%', label: 'Uptime monitoring' },
        { value: '24/7', label: 'Real-time alerts' },
        { value: '500+', label: 'Assets monitored' },
        { value: '<1min', label: 'Response time' },
      ],
    }}
    overview={{
      eyebrow: 'Integration highlights',
      title: 'Plug-and-play with industry-standard and legacy devices',
      lede: 'Fast onboarding, encrypted transmission, and robust error handling built in.',
      body: [
        'Our highly scalable ingestion platform supports flexible integration, robust security, and future-ready protocols — for smart factories, renewable projects, and energy grids. Get actionable intelligence from your data, regardless of scale, format, or connectivity.',
      ],
      checklist: [
        'Collect from PLCs, RTUs, meters, weather stations, and more',
        'No vendor lock-in across protocols or device generations',
        'All data links authenticated and encrypted',
        'Meets strict compliance standards',
        'Start with ten devices or ten thousand',
        'Ingestion layer scales with your portfolio',
      ],
      image: '/assets/img/general/photovoltaic-4541308_1280.jpg',
      imageAlt: 'Instrumented photovoltaic installation',
      imageCaption: 'One ingestion layer across every protocol, site and device generation',
    }}
    capabilities={{
      eyebrow: 'What you get',
      title: 'Enterprise-grade ingestion',
      lede: 'Resilience, observability, and governance built in.',
      items: [
        {
          icon: Activity,
          title: 'Real-time monitoring',
          description: 'Continuous monitoring of asset performance with instant alerts and notifications.',
        },
        {
          icon: BarChart3,
          title: 'Advanced analytics',
          description: 'Comprehensive data analysis with predictive insights and trend identification.',
        },
        {
          icon: Shield,
          title: 'Risk assessment',
          description: 'Proactive identification of potential issues before they impact performance.',
        },
        {
          icon: TrendingUp,
          title: 'Performance optimization',
          description: 'AI-driven recommendations to maximize efficiency and output.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Architecture',
      title: 'Built for any protocol, at any scale',
      lede: 'Three properties the ingestion layer holds regardless of how your estate is wired.',
      items: [
        {
          icon: Plug,
          title: 'Any protocol, any source',
          description: 'Collect from PLCs, RTUs, meters, weather stations, and more. No vendor lock-in.',
        },
        {
          icon: Lock,
          title: 'Secure by default',
          description: 'All data links authenticated and encrypted. Meets strict compliance standards.',
        },
        {
          icon: Layers,
          title: 'Ready for scale',
          description: 'Start with ten devices or ten thousand — our ingestion layer grows with your needs.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Outcomes',
      title: 'What reliable data makes possible',
      lede: 'Benefits operators realise once every asset reports on a common, trusted feed.',
      items: [
        {
          icon: Activity,
          title: 'Reduce downtime by up to 40%',
          description: 'Faults surface from live telemetry rather than from the next scheduled inspection.',
        },
        {
          icon: TrendingUp,
          title: 'Increase energy output by 15-25%',
          description: 'Underperformance is detected and corrected while the generating window is still open.',
        },
        {
          icon: BarChart3,
          title: 'Lower maintenance costs by 30%',
          description: 'Interventions are planned against condition data instead of fixed calendar intervals.',
        },
        {
          icon: Shield,
          title: 'Improve asset lifespan by 20%',
          description: 'Equipment runs inside its design envelope because deviations are caught early.',
        },
      ],
    }}
    cta={{
      title: 'Ready to streamline your data ingestion?',
      body: 'Talk to our experts and see how quickly we can connect your assets.',
      primary: { label: 'Get a demo', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default DataIngestionPage;
